import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
  Tooltip,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  TableSortLabel,
} from '@mui/material';
import {
  CloudUpload as UploadIcon,
  PictureAsPdf as PdfIcon,
  Delete as DeleteIcon,
  Visibility as ViewIcon,
  CheckCircle as PaidIcon,
  Download as DownloadIcon,
  PendingActions as PendingIcon,
  Search as SearchIcon,
  FileDownload as ExportIcon,
} from '@mui/icons-material';
import { externalDocumentsService } from '../services/api';
import UploadDocumentDialog from '../components/UploadDocumentDialog';

const ExternalDocuments = () => {
  const [documents, setDocuments] = useState([]);
  const [filteredDocuments, setFilteredDocuments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [uploadDialogOpen, setUploadDialogOpen] = useState(false);
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [selectedDocument, setSelectedDocument] = useState(null);
  const [stats, setStats] = useState(null);
  
  // Filtres et tri
  const [searchTerm, setSearchTerm] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [orderBy, setOrderBy] = useState('documentDate');
  const [order, setOrder] = useState('desc');

  const categories = [
    'Fournitures bureau',
    'Matériel informatique',
    'Frais véhicule',
    'Repas/Restaurant',
    'Hébergement',
    'Télécom/Internet',
    'Assurances',
    'Banque/Frais financiers',
    'Formation',
    'Logiciels/Outils',
    'Maintenance/Réparation',
    'Marketing/Publicité',
    'Électricité/Eau',
    'Loyer',
    'Autres charges',
  ];

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      const [docs, statsData] = await Promise.all([
        externalDocumentsService.getAll(),
        externalDocumentsService.getStats(),
      ]);
      setDocuments(docs);
      setStats(statsData);
    } catch (error) {
      console.error('Erreur lors du chargement des données:', error);
    } finally {
      setLoading(false);
    }
  };

  const applyFiltersAndSort = useCallback(() => {
    let filtered = [...documents];

    // Recherche textuelle
    if (searchTerm) {
      filtered = filtered.filter(doc =>
        doc.supplierName.toLowerCase().includes(searchTerm.toLowerCase()) ||
        doc.description?.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    // Filtre par catégorie
    if (categoryFilter) {
      filtered = filtered.filter(doc => doc.category === categoryFilter);
    }

    // Filtre par statut
    if (statusFilter === 'paid') {
      filtered = filtered.filter(doc => doc.isPaid);
    } else if (statusFilter === 'pending') {
      filtered = filtered.filter(doc => !doc.isPaid);
    }

    // Tri
    filtered.sort((a, b) => {
      let aValue = a[orderBy];
      let bValue = b[orderBy];

      if (orderBy === 'documentDate') {
        aValue = new Date(aValue);
        bValue = new Date(bValue);
      }

      if (order === 'asc') {
        return aValue > bValue ? 1 : -1;
      } else {
        return aValue < bValue ? 1 : -1;
      }
    });

    setFilteredDocuments(filtered);
  }, [documents, searchTerm, categoryFilter, statusFilter, orderBy, order]);

  useEffect(() => {
    applyFiltersAndSort();
  }, [applyFiltersAndSort]);

  const handleRequestSort = (property) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  const exportToCSV = () => {
    const headers = [
      'Fournisseur',
      'Catégorie',
      'Date',
      'Montant HT',
      'Taux TVA',
      'Montant TVA',
      'Total TTC',
      'Statut',
      'Date paiement',
      'Moyen paiement',
      'Description',
      'Notes'
    ];

    const rows = filteredDocuments.map(doc => [
      doc.supplierName,
      doc.category,
      formatDate(doc.documentDate),
      doc.amount,
      doc.vatRate,
      doc.vatAmount,
      doc.totalTTC,
      doc.isPaid ? 'Payé' : 'En attente',
      doc.paymentDate ? formatDate(doc.paymentDate) : '',
      doc.paymentMethod || '',
      doc.description || '',
      doc.notes || ''
    ]);

    const csvContent = [
      headers.join(';'),
      ...rows.map(row => row.join(';'))
    ].join('\n');

    const blob = new Blob(['\uFEFF' + csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `factures-fournisseurs-${new Date().toISOString().split('T')[0]}.csv`;
    link.click();
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Êtes-vous sûr de vouloir supprimer ce document ?')) {
      return;
    }

    try {
      await externalDocumentsService.delete(id);
      loadData();
    } catch (error) {
      console.error('Erreur lors de la suppression:', error);
    }
  };

  const handleView = (document) => {
    setSelectedDocument(document);
    setViewDialogOpen(true);
  };

  const handleDownload = async (document) => {
    try {
      const blob = await externalDocumentsService.download(document.id);
      const url = window.URL.createObjectURL(blob);
      const link = window.document.createElement('a');
      link.href = url;
      link.download = document.originalName || `document-${document.id}.pdf`;
      link.click();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Erreur lors du téléchargement:', error);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('fr-FR');
  };

  const formatAmount = (amount) => {
    return new Intl.NumberFormat('fr-FR', {
      style: 'currency',
      currency: 'EUR',
    }).format(amount);
  };

  return (
    <Box>
      {/* En-tête */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4" fontWeight="bold">
          Archives Fournisseurs
        </Typography>
        <Box display="flex" gap={2}>
          <Button
            variant="outlined"
            startIcon={<ExportIcon />}
            onClick={exportToCSV}
            disabled={filteredDocuments.length === 0}
          >
            Export CSV
          </Button>
          <Button
            variant="contained"
            startIcon={<UploadIcon />}
            onClick={() => setUploadDialogOpen(true)}
            size="large"
          >
            Upload Facture
          </Button>
        </Box>
      </Box>

      {/* Filtres */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} sm={6} md={3}>
              <TextField
                fullWidth
                size="small"
                placeholder="Rechercher..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                InputProps={{
                  startAdornment: <SearchIcon sx={{ mr: 1, color: 'text.secondary' }} />,
                }}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <FormControl fullWidth size="small">
                <InputLabel>Catégorie</InputLabel>
                <Select
                  value={categoryFilter}
                  onChange={(e) => setCategoryFilter(e.target.value)}
                  label="Catégorie"
                >
                  <MenuItem value="">Toutes</MenuItem>
                  {categories.map((cat) => (
                    <MenuItem key={cat} value={cat}>{cat}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <FormControl fullWidth size="small">
                <InputLabel>Statut</InputLabel>
                <Select
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                  label="Statut"
                >
                  <MenuItem value="">Tous</MenuItem>
                  <MenuItem value="paid">Payé</MenuItem>
                  <MenuItem value="pending">En attente</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Typography variant="body2" color="text.secondary">
                {filteredDocuments.length} document(s) affiché(s)
              </Typography>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Statistiques */}
      {stats && (
        <Grid container spacing={3} mb={3}>
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Typography color="text.secondary" gutterBottom>
                  Total Dépenses
                </Typography>
                <Typography variant="h5" fontWeight="bold">
                  {formatAmount(stats.totalExpenses || 0)}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {stats.totalCount || 0} documents
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Typography color="text.secondary" gutterBottom>
                  Payées
                </Typography>
                <Typography variant="h5" fontWeight="bold" color="success.main">
                  {formatAmount(stats.paidExpenses || 0)}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Typography color="text.secondary" gutterBottom>
                  En attente
                </Typography>
                <Typography variant="h5" fontWeight="bold" color="warning.main">
                  {formatAmount(stats.pendingExpenses || 0)}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}

      {/* Tableau des documents */}
      <Card>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>
                  <TableSortLabel
                    active={orderBy === 'supplierName'}
                    direction={orderBy === 'supplierName' ? order : 'asc'}
                    onClick={() => handleRequestSort('supplierName')}
                  >
                    Fournisseur
                  </TableSortLabel>
                </TableCell>
                <TableCell>
                  <TableSortLabel
                    active={orderBy === 'category'}
                    direction={orderBy === 'category' ? order : 'asc'}
                    onClick={() => handleRequestSort('category')}
                  >
                    Catégorie
                  </TableSortLabel>
                </TableCell>
                <TableCell>
                  <TableSortLabel
                    active={orderBy === 'documentDate'}
                    direction={orderBy === 'documentDate' ? order : 'asc'}
                    onClick={() => handleRequestSort('documentDate')}
                    sx={{
                      color: 'common.white',
                      '&.Mui-active': { color: 'common.white' },
                      '& .MuiTableSortLabel-icon': { color: 'common.white !important' },
                    }}
                  >
                    Date
                  </TableSortLabel>
                </TableCell>
                <TableCell align="right">
                  <TableSortLabel
                    active={orderBy === 'amount'}
                    direction={orderBy === 'amount' ? order : 'asc'}
                    onClick={() => handleRequestSort('amount')}
                  >
                    Montant HT
                  </TableSortLabel>
                </TableCell>
                <TableCell align="right">TVA</TableCell>
                <TableCell align="right">
                  <TableSortLabel
                    active={orderBy === 'totalTTC'}
                    direction={orderBy === 'totalTTC' ? order : 'asc'}
                    onClick={() => handleRequestSort('totalTTC')}
                  >
                    Total TTC
                  </TableSortLabel>
                </TableCell>
                <TableCell>Statut</TableCell>
                <TableCell>Fichier</TableCell>
                <TableCell align="center">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={9} align="center">
                    <CircularProgress />
                  </TableCell>
                </TableRow>
              ) : filteredDocuments.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={9} align="center">
                    <Typography color="text.secondary" py={4}>
                      {documents.length === 0
                        ? 'Aucun document. Cliquez sur "Upload Facture" pour commencer.'
                        : 'Aucun document ne correspond aux filtres.'}
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                filteredDocuments.map((doc) => (
                  <TableRow key={doc.id} hover>
                    <TableCell>
                      <Typography fontWeight="bold">{doc.supplierName}</Typography>
                      {doc.description && (
                        <Typography variant="caption" color="text.secondary">
                          {doc.description}
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      <Chip label={doc.category} size="small" color="primary" variant="outlined" />
                    </TableCell>
                    <TableCell>{formatDate(doc.documentDate)}</TableCell>
                    <TableCell align="right">{formatAmount(doc.amount)}</TableCell>
                    <TableCell align="right">
                      {formatAmount(doc.vatAmount)} ({doc.vatRate}%)
                    </TableCell>
                    <TableCell align="right">
                      <Typography fontWeight="bold">{formatAmount(doc.totalTTC)}</Typography>
                    </TableCell>
                    <TableCell>
                      {doc.isPaid ? (
                        <Chip
                          icon={<PaidIcon />}
                          label="Payé"
                          size="small"
                          color="success"
                        />
                      ) : (
                        <Chip
                          icon={<PendingIcon />}
                          label="En attente"
                          size="small"
                          color="warning"
                        />
                      )}
                    </TableCell>
                    <TableCell>
                      {doc.filePath ? (
                        <Chip
                          icon={<PdfIcon />}
                          label="PDF"
                          size="small"
                          color="error"
                          variant="outlined"
                        />
                      ) : (
                        <Chip label="Aucun" size="small" variant="outlined" />
                      )}
                    </TableCell>
                    <TableCell align="center">
                      <Box display="flex" gap={0.5}>
                        {doc.filePath && (
                          <>
                            <Tooltip title="Visualiser">
                              <IconButton size="small" onClick={() => handleView(doc)}>
                                <ViewIcon />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Télécharger">
                              <IconButton size="small" onClick={() => handleDownload(doc)}>
                                <DownloadIcon />
                              </IconButton>
                            </Tooltip>
                          </>
                        )}
                        <Tooltip title="Supprimer">
                          <IconButton
                            size="small"
                            color="error"
                            onClick={() => handleDelete(doc.id)}
                          >
                            <DeleteIcon />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Card>

      {/* Dialog Upload */}
      <UploadDocumentDialog
        open={uploadDialogOpen}
        onClose={() => setUploadDialogOpen(false)}
        onSuccess={loadData}
      />

      {/* Dialog Visualisation */}
      <Dialog
        open={viewDialogOpen}
        onClose={() => setViewDialogOpen(false)}
        maxWidth="lg"
        fullWidth
      >
        <DialogTitle>
          Visualisation - {selectedDocument?.supplierName}
        </DialogTitle>
        <DialogContent dividers>
          {selectedDocument?.filePath && (
            <Box sx={{ height: '70vh' }}>
              <iframe
                src={`${selectedDocument.filePath}`}
                width="100%"
                height="100%"
                title="Document PDF"
                style={{ border: 'none' }}
              />
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setViewDialogOpen(false)}>Fermer</Button>
          {selectedDocument && (
            <Button
              variant="contained"
              startIcon={<DownloadIcon />}
              onClick={() => handleDownload(selectedDocument)}
            >
              Télécharger
            </Button>
          )}
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ExternalDocuments;
